<script setup lang="ts">
import { brandRef } from "../brand-state";
/**
 * Who signs, owns and decides — named, and editable.
 *
 * The to-do list says "Agreement signers", "Data owners", "AI Board"; this
 * says who they are. Each role starts from a placeholder in
 * `config/responsibilities.yaml` (or the name the agreement itself gives)
 * and an admin replaces it here, audited, with no deploy.
 *
 * The e-mail is what matters: a person with one sees their items under
 * "My sign-offs" and signs for themselves once they can sign in to DOIT AI.
 * Without one they are a placeholder, and an admin records on their behalf.
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type ResponsibilitiesResponse,
  type ResponsibilityRole,
  type RolePerson,
  getResponsibilities,
  setResponsibility,
} from "../api/admin";
import { useToast } from "../composables/useToast";
import FoldSection from "./FoldSection.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();

const data = ref<ResponsibilitiesResponse | null>(null);
async function load(): Promise<void> {
  try {
    data.value = await getResponsibilities(props.devUser);
  } catch (e) {
    toast.error(
      "Could not load responsibilities",
      e instanceof AdminError ? e.message : String(e),
    );
  }
}
onMounted(load);

const GROUPS: { kind: ResponsibilityRole["kind"]; title: string }[] = [
  { kind: "signer", title: "Agreement signers" },
  { kind: "data-owner", title: "Data owners" },
  { kind: "board", title: "AI Board" },
];

function rolesOf(kind: ResponsibilityRole["kind"]): ResponsibilityRole[] {
  return (data.value?.roles ?? []).filter((r) => r.kind === kind);
}

const placeholders = computed(
  () => (data.value?.roles ?? []).filter((r) => !r.named).length,
);

const SOURCE_TEXT: Record<ResponsibilityRole["source"], string> = {
  admin: "",
  config: "placeholder",
  document: "from the agreement",
};

// ── Editing one role ──────────────────────────────────────────────────────

const editing = ref<string | null>(null);
const draft = ref<RolePerson[]>([]);
const saving = ref(false);

function edit(r: ResponsibilityRole): void {
  editing.value = r.key;
  draft.value = r.people.length
    ? r.people.map((p) => ({ ...p }))
    : [{ name: "", email: "" }];
}

/** Picking a known user by e-mail fills in their name. */
function onEmail(p: RolePerson): void {
  const hit = data.value?.known_users.find(
    (u) => u.email.toLowerCase() === p.email.trim().toLowerCase(),
  );
  if (hit && (!p.name || p.name === p.email)) p.name = hit.name;
}

async function save(key: string, people: RolePerson[]): Promise<void> {
  saving.value = true;
  try {
    const r = await setResponsibility(
      key,
      people
        .map((p) => ({ name: p.name.trim(), email: p.email.trim() }))
        .filter((p) => p.name || p.email),
      props.devUser,
    );
    if (data.value) data.value.roles = r.roles;
    editing.value = null;
    toast.success("Saved");
  } catch (e) {
    toast.error(
      "Could not save",
      e instanceof AdminError ? e.message : String(e),
    );
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <FoldSection
    class="card"
    title="Who signs"
    :level="3"
    :summary="
      data
        ? placeholders
          ? `${placeholders} of ${data.roles.length} roles have no e-mail yet`
          : 'everyone named'
        : ''
    "
    :tone="placeholders ? 'warn' : 'neutral'"
  >
    <p class="hint">
      Who signs each agreement, owns each source and sits on the AI Board.
      Someone with an e-mail sees their items under <em>My sign-offs</em> and
      signs for themselves once they can sign in to {{ brandRef.productName }}; without one an
      admin records on their behalf.
    </p>
    <datalist id="known-users">
      <option
        v-for="u in data?.known_users ?? []"
        :key="u.email"
        :value="u.email"
      >
        {{ u.name }}
      </option>
    </datalist>

    <div v-for="g in GROUPS" :key="g.kind" class="group">
      <h4>{{ g.title }}</h4>
      <table class="roles">
        <tr v-for="r in rolesOf(g.kind)" :key="r.key">
          <td class="roles__label">
            {{
              r.kind === "data-owner"
                ? r.label.replace(/^Data owner — /, "")
                : r.label
            }}
            <div v-if="r.kind === 'signer'" class="muted small">
              in {{ r.agreements.length }}
              {{ r.agreements.length === 1 ? "agreement" : "agreements" }}
            </div>
          </td>
          <td>
            <template v-if="editing !== r.key">
              <span
                v-for="p in r.people"
                :key="p.name + p.email"
                class="person"
              >
                {{ p.name }}
                <span v-if="p.email" class="muted small">{{ p.email }}</span>
              </span>
              <span v-if="!r.people.length" class="muted">nobody</span>
              <!-- "placeholder" means nobody who can act: no e-mail. A name
                   with an e-mail from the config file is a real person. -->
              <span v-if="!r.named" class="tag">{{
                r.source === "admin" ? "no e-mail" : SOURCE_TEXT[r.source]
              }}</span>
            </template>
            <form v-write v-else class="editor" @submit.prevent="save(r.key, draft)">
              <div v-for="(p, i) in draft" :key="i" class="editor__row">
                <input v-model="p.name" placeholder="Name" maxlength="200" />
                <input
                  v-model="p.email"
                  type="email"
                  list="known-users"
                  placeholder="e-mail (optional)"
                  maxlength="320"
                  @change="onEmail(p)"
                />
                <button
                  type="button"
                  class="link muted"
                  @click="draft.splice(i, 1)"
                >
                  remove
                </button>
              </div>
              <div class="editor__actions">
                <button
                  v-if="r.kind === 'board' || draft.length < 3"
                  type="button"
                  class="link"
                  @click="draft.push({ name: '', email: '' })"
                >
                  + add person
                </button>
                <span class="spacer" />
                <button
                  v-if="r.source === 'admin'"
                  type="button"
                  class="btn btn--ghost"
                  :disabled="saving"
                  title="Forget the names saved here and use the placeholder again"
                  @click="save(r.key, [])"
                >
                  Reset
                </button>
                <button type="button" class="btn" @click="editing = null">
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn btn--primary"
                  :disabled="saving"
                >
                  Save
                </button>
              </div>
            </form>
          </td>
          <td class="roles__act">
            <button v-write v-if="editing !== r.key" class="link" @click="edit(r)">
              edit
            </button>
          </td>
        </tr>
      </table>
    </div>
  </FoldSection>
</template>

<style scoped>
.hint {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.group h4 {
  margin: 0.8rem 0 0.3rem;
  font-size: 0.85rem;
}
.roles {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.roles td {
  padding: 0.35rem 0.6rem 0.35rem 0;
  border-top: 1px solid var(--color-border);
  vertical-align: top;
}
.roles__label {
  width: 34%;
}
.roles__act {
  width: 3rem;
  text-align: right;
}
.person {
  display: inline-flex;
  gap: 0.3rem;
  align-items: baseline;
  margin-right: 0.8rem;
}
.tag {
  padding: 0 0.35rem;
  border-radius: 3px;
  font-size: 0.72rem;
  border: 1px dashed var(--color-muted);
  color: var(--color-muted);
}
.editor {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.editor__row {
  display: grid;
  grid-template-columns: 1fr 1.2fr auto;
  gap: 0.4rem;
}
.editor__row input {
  min-width: 0;
  font: inherit;
  font-size: 0.86rem;
  padding: 0.25rem 0.45rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
}
.editor__actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.spacer {
  flex: 1;
}
.muted {
  color: var(--color-muted);
}
.small {
  font-size: 0.78rem;
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  padding: 0;
  font: inherit;
  font-size: 0.85rem;
}
button.link.muted {
  color: var(--color-muted);
}
</style>
